import { useState, type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as FilesIcon } from '../../../../assets/icons/external-documents.svg';
import { ReactComponent as ReviewGlyph } from '../../../../assets/icons/status-review.svg';
import type { BenefitsDocumentsSource } from '../../../constants/tagManager';
import type { IQuote } from '../../../interfaces/quotes';
import { TRANSLATIONS_EXTERNAL_BENEFITS } from '../../../translations/commons';
import { getExternalBenefitsState, getExternalBenefitsSubmittedAt } from '../../../utils/quotes/quotes';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import SetupSection from '../../SetupSection/SetupSection';
import SetupSlot from '../../SetupSlot/SetupSlot';
import ExternalBenefitsDocumentsModal from '../ExternalBenefitsDocumentsModal/ExternalBenefitsDocumentsModal';

import './ExternalBenefitsDocs.scss';

const BLOCK = TRANSLATIONS_EXTERNAL_BENEFITS.block;

export interface ExternalBenefitsDocsProps {
  quote: IQuote;
  brokerId: string;
  prospectId: number;
  source: BenefitsDocumentsSource;
  layout?: 'summary' | 'record';
  onSubmitted: (updated: IQuote) => void;
}

const ExternalBenefitsDocs = ({
  quote,
  brokerId,
  prospectId,
  source,
  layout = 'record',
  onSubmitted
}: ExternalBenefitsDocsProps) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const state = getExternalBenefitsState(quote);
  const submittedAt = getExternalBenefitsSubmittedAt(quote);

  const formattedDate = submittedAt
    ? new Date(submittedAt).toLocaleDateString(i18n.language || 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : null;

  const content: { icon: ReactNode; title: string; description: string; cta: string } =
    state === 'none'
      ? {
          icon: <FilesIcon />,
          title: t(BLOCK.none.title),
          description: t(BLOCK.none.description),
          cta: t(BLOCK.none.cta)
        }
      : {
          icon: <ReviewGlyph width={18} height={18} />,
          title: t(BLOCK.inReview.title),
          description: formattedDate
            ? t(BLOCK.inReview.description, { date: formattedDate })
            : t(BLOCK.inReview.descriptionNoDate),
          cta: t(BLOCK.inReview.cta)
        };

  const row = (
    <SetupSlot icon={content.icon} title={content.title} description={content.description}>
      <CustomButton variant="default" size={state === 'none' ? 's' : 'xs'} onClick={() => setOpen(true)}>
        {content.cta}
      </CustomButton>
    </SetupSlot>
  );

  const modal = (
    <ExternalBenefitsDocumentsModal
      open={open}
      quote={quote}
      brokerId={brokerId}
      prospectId={prospectId}
      source={source}
      onClose={() => setOpen(false)}
      onSubmitted={onSubmitted}
    />
  );

  if (layout === 'summary') {
    return (
      <>
        <SetupSection
          title={
            <>
              {t(BLOCK.title)} <span className="external-benefits-docs__optional">{t(BLOCK.optional)}</span>
            </>
          }
        >
          {row}
        </SetupSection>
        {modal}
      </>
    );
  }

  return (
    <section className="external-benefits-docs external-benefits-docs--record">
      <h4 className="external-benefits-docs__record-title">{t(BLOCK.title)}</h4>
      {row}
      {modal}
    </section>
  );
};

export default ExternalBenefitsDocs;
